// T-066 — Compressione delle immagini lato client prima dell'upload alla
// edge function `fiscal-photo`.
//
// Perché: oggi una foto della fattura da fotocamera moderna può pesare
// 8–12 MB. Senza compressione 1) il base64 nel body dell'edge function
// supera spesso il limite di 6 MB di Supabase, 2) il modello AI (Gemini)
// riceve un'immagine inutilmente enorme che rallenta l'estrazione e
// consuma più quota, 3) lo Storage si riempie di file pesanti.
//
// Cosa: ridimensioniamo a max `maxDim` lato lungo (default 2000px,
// sufficiente per OCR ricevute/fatture) e ricodifichiamo in JPEG con
// qualità `quality` (default 0.7). Tipica riduzione 8 MB → ~400-700 KB
// senza perdita visibile di leggibilità.
//
// I PDF NON vengono ricompressi (richiederebbe pdf.js + canvas re-render
// pagina per pagina): passano dritti. La maggior parte delle fatture in
// PDF è già <500 KB perché generate da gestionale, quindi il problema
// non esiste in pratica.

export interface CompressedFile {
  // Base64 SENZA prefisso data:.
  b64: string;
  mime: string;
  sizeBytes: number;
  // True quando l'input è stato effettivamente ricodificato.
  compressed: boolean;
}

/**
 * Comprime un'immagine. Se non comprimibile (PDF, errore, ecc.) ritorna
 * l'input originale così com'è.
 *
 * @param b64 base64 SENZA prefisso `data:...;base64,`
 * @param mime mime-type dell'input
 */
export async function compressImageIfNeeded(
  b64: string,
  mime: string,
  opts: { maxDim?: number; quality?: number } = {},
): Promise<CompressedFile> {
  const maxDim = opts.maxDim ?? 2000;
  const quality = opts.quality ?? 0.7;
  const originalSize = Math.floor((b64.length * 3) / 4);

  // PDF: non comprimibile lato client (servirebbe pdf.js full pipeline).
  // Passa dritto.
  if (mime === 'application/pdf') {
    return { b64, mime, sizeBytes: originalSize, compressed: false };
  }

  // Non immagine? Niente da fare.
  if (!mime.startsWith('image/')) {
    return { b64, mime, sizeBytes: originalSize, compressed: false };
  }

  try {
    const img = await loadImageFromBase64(b64, mime);
    // Già piccola? Salta la ricodifica (evitiamo perdita di qualità inutile
    // su immagini già ottimizzate, es. screenshot piccoli).
    const longestSide = Math.max(img.width, img.height);
    if (longestSide <= maxDim && originalSize <= 800_000) {
      return { b64, mime, sizeBytes: originalSize, compressed: false };
    }

    // Ridimensiona mantenendo aspect ratio.
    let targetW = img.width;
    let targetH = img.height;
    if (longestSide > maxDim) {
      const ratio = maxDim / longestSide;
      targetW = Math.round(img.width * ratio);
      targetH = Math.round(img.height * ratio);
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return { b64, mime, sizeBytes: originalSize, compressed: false };
    }
    // Sfondo bianco per JPEG (no canale alpha → sennò pixel trasparenti
    // diventano neri).
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetW, targetH);
    ctx.drawImage(img, 0, 0, targetW, targetH);

    // toDataURL → base64. Forza JPEG sempre (più piccolo del PNG).
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    const idx = dataUrl.indexOf(',');
    if (idx < 0) {
      return { b64, mime, sizeBytes: originalSize, compressed: false };
    }
    const compressedB64 = dataUrl.slice(idx + 1);
    const compressedSize = Math.floor((compressedB64.length * 3) / 4);

    // Se la compressione paradossalmente ha ingrandito (immagine già
    // molto piccola), tieni l'originale.
    if (compressedSize >= originalSize) {
      return { b64, mime, sizeBytes: originalSize, compressed: false };
    }

    return {
      b64: compressedB64,
      mime: 'image/jpeg',
      sizeBytes: compressedSize,
      compressed: true,
    };
  } catch (err) {
    // Qualsiasi errore → fallback all'originale, niente blocco.
    // eslint-disable-next-line no-console
    console.warn('[imageCompress] compressione fallita, uso originale', err);
    return { b64, mime, sizeBytes: originalSize, compressed: false };
  }
}

// T-373 — Impagina una foto su una tela di rapporto fisso (default 16:10, lo
// stesso del riquadro dettaglio del menù pubblico `.dish-modal-photo`) così che
// il `background-size:cover` non tagli il soggetto.
//
// Due modalità:
//  - **auto-pad** (default): immagine `contain` centrata su sfondo `bg` (colore
//    `paper` del tema, fallback bianco). Ideale per foto prodotto su fondo neutro.
//  - **crop** (ritocco pan/zoom): se `crop` è dato, disegna quella regione
//    sorgente a piena tela (`cover`), senza bande.
export interface PadToRatioOptions {
  ratio?: number; // width/height, default 16/10
  bg?: string; // colore padding, default '#ffffff'
  maxWidth?: number; // larghezza canvas, default 1200
  quality?: number; // JPEG quality, default 0.82
  maxBytes?: number; // limite JPEG, default 250 KB
  crop?: { sx: number; sy: number; sw: number; sh: number }; // regione sorgente (px)
}

export const MENU_PHOTO_MAX_WIDTH = 1200;
export const MENU_PHOTO_MAX_BYTES = 250_000;

export async function padToRatio(
  b64: string,
  mime: string,
  opts: PadToRatioOptions = {},
): Promise<Blob> {
  const ratio = opts.ratio ?? 16 / 10;
  const bg = opts.bg ?? '#ffffff';
  const maxWidth = opts.maxWidth ?? MENU_PHOTO_MAX_WIDTH;
  const quality = opts.quality ?? 0.82;
  const maxBytes = opts.maxBytes ?? MENU_PHOTO_MAX_BYTES;

  const img = await loadImageFromBase64(b64, mime);
  const minWidth = Math.min(maxWidth, 600);
  const widths = [...new Set(
    [1, 0.9, 0.8, 0.7, 0.6, 0.5].map((scale) => Math.max(minWidth, Math.round(maxWidth * scale))),
  )];
  const qualities = [...new Set(
    [quality, 0.74, 0.66, 0.58, 0.5, 0.42]
      .map((q) => Math.min(quality, q))
      .map((q) => Math.min(0.92, Math.max(0.35, q))),
  )];
  let smallest: Blob | null = null;

  for (const cw of widths) {
    const ch = Math.round(cw / ratio);
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas_unavailable');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, cw, ch);

    if (opts.crop) {
      // Ritaglio pan/zoom → riempi tutta la tela (cover, niente bande).
      const { sx, sy, sw, sh } = opts.crop;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
    } else {
      // Auto-pad: contain centrato.
      const scale = Math.min(cw / img.width, ch / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    }

    for (const jpegQuality of qualities) {
      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob((value) => resolve(value), 'image/jpeg', jpegQuality),
      );
      if (!blob) throw new Error('encode_failed');
      if (!smallest || blob.size < smallest.size) smallest = blob;
      if (blob.size <= maxBytes) return blob;
    }
  }

  if (!smallest || smallest.size > maxBytes) throw new Error('menu_photo_too_large');
  return smallest;
}

async function loadImageFromBase64(b64: string, mime: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image_load_failed'));
    img.src = `data:${mime};base64,${b64}`;
  });
}
